/**
 * repositories/emailTemplatesRepository.ts
 * Email Templates table data access layer
 *
 * REFERENCE IMPLEMENTATION - Follows enhanced pattern from betaApplicationsRepository.ts
 *
 * Architecture: WU1.5 Task 7 - EmailTemplate Repository Pattern
 * Enhanced patterns applied:
 * - Column projection as default
 * - Optimistic concurrency on all updates
 * - Enhanced pagination with withTotal flag
 * - Explicit null vs undefined policy (toPatch for updates)
 * - Comprehensive error handling
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  EmailTemplate,
  EmailTemplateInsert,
  EmailTemplateUpdate,
  AllEmailTemplateTypes
} from '@/types/supabase/EmailTemplate';

// PATTERN: Import wire types from generated schema
type EmailTemplateRow = Database['public']['Tables']['email_templates']['Row'];
type EmailTemplateInsertRow = Database['public']['Tables']['email_templates']['Insert'];
type EmailTemplateUpdateRow = Database['public']['Tables']['email_templates']['Update'];

/**
 * ============================================================================
 * REPOSITORY RULES (enhanced patterns for all repositories)
 * ============================================================================
 *
 * 1. **Select only needed columns (PROJECTION AS DEFAULT)**
 *    - ALWAYS use explicit column lists: .select('id, subject, content, ...')
 *    - Can alias columns: .select('id, categoryId:category_id')
 *    - Still run through toDomain() for consistency
 *    - Keeps queries efficient, standardizes pattern
 *
 * 2. **Always map snake⇄camel at the boundary**
 *    - Database returns snake_case (wire types)
 *    - Repository returns camelCase (domain types)
 *    - Use toDomain() for reads, toWire() for inserts, toPatch() for updates
 *
 * 3. **PARTIAL UPDATE HYGIENE: undefined = omit; null = clear column**
 *    - ⚠️ CRITICAL: Use toPatch() for ALL updates, NEVER toWire()
 *    - toPatch() strips undefined keys automatically (omit from PATCH)
 *    - Passing null will set column to NULL in database (clear value)
 *    - This prevents accidental data loss on partial updates
 *    - MANTRA: "toPatch for updates, never toWire"
 *
 * 4. **Optimistic concurrency for updates**
 *    - Include updatedAt in where clause: .eq('updated_at', prevUpdatedAt)
 *    - If 0 rows updated → throw ConflictError with reload data
 *    - Service layer catches and maps to friendly UI message
 *    - Prevents admin race conditions (concurrent edits)
 *
 * 5. **Error handling**
 *    - Throw on Supabase errors (let caller handle)
 *    - Return null for "not found" on single lookups
 *    - Return empty array for "not found" on list queries
 *
 * 6. **Database Constraints** (GPT Enhancement #1)
 *    - ⚠️ RECOMMENDATION: Add UNIQUE(category_id, template_type) constraint
 *    - Currently enforced in application logic only (getByType assumes unique)
 *    - Future migration should add DB-level uniqueness for safety
 *    - TODO: Create Supabase migration for unique constraint
 *
 * ============================================================================
 */

/**
 * Projection constant - primary columns for most queries
 */
const TEMPLATE_PROJECTION = 'id,category_id,template_type,subject,content,signature,created_at,updated_at,updated_by' as const;

/**
 * Conflict error thrown when optimistic concurrency check fails
 */
export class ConflictError extends Error {
  constructor(message: string, public data?: { current?: EmailTemplate }) {
    super(message);
    this.name = 'ConflictError';
  }
}

/**
 * Email Templates Repository
 *
 * Provides type-safe data access for email_templates table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Enhanced with:
 * - Optimistic concurrency control
 * - Column projection for performance
 * - Enhanced pagination with withTotal flag
 * - Comprehensive error handling
 */
export const emailTemplatesRepository = {
  /**
   * Get template by ID
   *
   * PATTERN: Single record lookup with nullable return and column projection
   *
   * @param id - Template ID (UUID)
   * @returns Promise<EmailTemplate | null> - Template if found, null otherwise
   */
  async getById(id: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select(TEMPLATE_PROJECTION)
      .eq('id', id)
      .maybeSingle<EmailTemplateRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<EmailTemplate>(data);
  },

  /**
   * Get template by type
   *
   * PATTERN: Unique lookup by business key (template_type)
   *
   * NOTE: This assumes template_type is unique per category.
   * Currently enforced in application logic only.
   * RECOMMENDATION: Add UNIQUE(category_id, template_type) DB constraint.
   *
   * @param templateType - Template type to retrieve
   * @returns Promise<EmailTemplate | null> - Template if found, null otherwise
   */
  async getByType(templateType: AllEmailTemplateTypes): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select(TEMPLATE_PROJECTION)
      .eq('template_type', templateType)
      .maybeSingle<EmailTemplateRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<EmailTemplate>(data);
  },

  /**
   * Get all templates grouped by type
   *
   * PATTERN: Map result for admin UI convenience
   *
   * Returns a map where keys are template types and values are templates.
   * Makes it easy for admin UI to access templates by type.
   *
   * @returns Promise<Record<string, EmailTemplate | null>> - Templates by type
   */
  async getAllTemplates(): Promise<Record<string, EmailTemplate | null>> {
    const { data, error } = await supabase
      .from('email_templates')
      .select(TEMPLATE_PROJECTION)
      .order('template_type');

    if (error) throw error;
    if (!data) return {};

    const templates = data.map(row => toDomain<EmailTemplate>(row));

    // Build map by template_type
    const templateMap: Record<string, EmailTemplate | null> = {};

    // Common template types for pre-population
    const commonTypes = ['approval', 'rejection', 'waitlist', 'confirmation', 'invitation', 'activation-reminder'];
    commonTypes.forEach(type => {
      templateMap[type] = templates.find(t => t.templateType === type) || null;
    });

    // Add any other templates found
    templates.forEach(template => {
      if (!templateMap[template.templateType]) {
        templateMap[template.templateType] = template;
      }
    });

    return templateMap;
  },

  /**
   * Get templates by category
   *
   * PATTERN: Filter query with optional pagination
   *
   * @param categoryId - Email category ID
   * @param opts - Optional list options
   * @returns Promise<EmailTemplate[]> - Templates in category
   */
  async getTemplatesByCategory(
    categoryId: string,
    opts: { limit?: number; offset?: number } = {}
  ): Promise<EmailTemplate[]> {
    const { limit, offset } = opts;

    let query = supabase
      .from('email_templates')
      .select(TEMPLATE_PROJECTION)
      .eq('category_id', categoryId)
      .order('template_type');

    if (limit !== undefined && offset !== undefined) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data, error } = await query;

    if (error) throw error;
    if (!data) return [];

    return data.map(row => toDomain<EmailTemplate>(row));
  },

  /**
   * List templates with pagination and filtering
   *
   * PATTERN: Enhanced pagination contract with withTotal flag
   *
   * @param opts - List options
   * @param opts.categoryId - Filter by category (optional)
   * @param opts.limit - Max results per page (default: 50)
   * @param opts.offset - Starting offset (default: 0)
   * @param opts.withTotal - Include total count (default: true)
   * @returns Promise<{ items: EmailTemplate[], total: number }> - Paginated results
   */
  async list(opts: {
    categoryId?: string;
    limit?: number;
    offset?: number;
    withTotal?: boolean;
  } = {}): Promise<{ items: EmailTemplate[]; total: number }> {
    const { categoryId, limit = 50, offset = 0, withTotal = true } = opts;

    // PATTERN: Build query with optional filters
    const selectOptions = withTotal ? { count: 'exact' as const } : {};
    let query = supabase
      .from('email_templates')
      .select(TEMPLATE_PROJECTION, selectOptions)
      .order('updated_at', { ascending: false });

    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }

    // PATTERN: Pagination with range (PostgreSQL LIMIT/OFFSET)
    const { data, error, count } = await query.range(offset, offset + limit - 1);

    if (error) throw error;
    if (!data) return { items: [], total: 0 };

    return {
      items: data.map(row => toDomain<EmailTemplate>(row)),
      total: count ?? 0
    };
  },

  /**
   * Create new template
   *
   * PATTERN: Insert with wire type conversion
   *
   * @param templateData - Template data (camelCase domain type)
   * @returns Promise<EmailTemplate> - Created template
   */
  async create(templateData: EmailTemplateInsert): Promise<EmailTemplate> {
    // PATTERN: Convert domain type (camelCase) to wire type (snake_case)
    const wireData = toWire<EmailTemplateInsertRow>(templateData);

    const { data, error } = await supabase
      .from('email_templates')
      .insert(wireData)
      .select(TEMPLATE_PROJECTION)
      .single<EmailTemplateRow>();

    if (error) throw error;
    if (!data) throw new Error('Insert succeeded but no data returned');

    return toDomain<EmailTemplate>(data);
  },

  /**
   * Update template by ID with optimistic concurrency
   *
   * PATTERN: Partial update with undefined stripping and concurrency check
   *
   * @param id - Template ID
   * @param updates - Partial template updates (camelCase domain type)
   * @param prevUpdatedAt - Previous updatedAt value for concurrency check
   * @returns Promise<EmailTemplate> - Updated template
   * @throws ConflictError if template was modified by another admin
   */
  async update(
    id: string,
    updates: EmailTemplateUpdate,
    prevUpdatedAt: string
  ): Promise<EmailTemplate> {
    // PATTERN: toPatch strips undefined (omit), keeps null (clear column)
    const wireUpdates = toPatch<EmailTemplateUpdateRow>(updates);

    // PATTERN: Optimistic concurrency - include updatedAt in where clause
    const { data, error } = await supabase
      .from('email_templates')
      .update(wireUpdates)
      .eq('id', id)
      .eq('updated_at', prevUpdatedAt) // Concurrency check
      .select(TEMPLATE_PROJECTION)
      .maybeSingle<EmailTemplateRow>();

    if (error) throw error;

    // PATTERN: If no rows updated, another admin modified it
    if (!data) {
      // Reload current state for UI refresh
      const current = await this.getById(id);
      throw new ConflictError(
        'Template was modified by another admin',
        current ? { current } : undefined
      );
    }

    return toDomain<EmailTemplate>(data);
  },

  /**
   * Update template by type with optimistic concurrency
   *
   * PATTERN: Convenience wrapper for type-based updates
   *
   * NOTE: This assumes template_type is unique per category.
   * See getByType() for DB constraint recommendation.
   *
   * @param templateType - Template type to update
   * @param updates - Partial template updates (camelCase domain type)
   * @param prevUpdatedAt - Previous updatedAt value for concurrency check
   * @returns Promise<EmailTemplate> - Updated template
   * @throws ConflictError if template was modified by another admin
   */
  async updateByType(
    templateType: AllEmailTemplateTypes,
    updates: EmailTemplateUpdate,
    prevUpdatedAt: string
  ): Promise<EmailTemplate> {
    // PATTERN: toPatch strips undefined (omit), keeps null (clear column)
    const wireUpdates = toPatch<EmailTemplateUpdateRow>(updates);

    // PATTERN: Optimistic concurrency - include updatedAt in where clause
    const { data, error } = await supabase
      .from('email_templates')
      .update(wireUpdates)
      .eq('template_type', templateType)
      .eq('updated_at', prevUpdatedAt) // Concurrency check
      .select(TEMPLATE_PROJECTION)
      .maybeSingle<EmailTemplateRow>();

    if (error) throw error;

    // PATTERN: If no rows updated, another admin modified it
    if (!data) {
      // Reload current state for UI refresh
      const current = await this.getByType(templateType);
      throw new ConflictError(
        'Template was modified by another admin',
        current ? { current } : undefined
      );
    }

    return toDomain<EmailTemplate>(data);
  },

  /**
   * Delete template
   *
   * PATTERN: Delete by ID with void return
   *
   * @param id - Template ID to delete
   * @returns Promise<void>
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Delete all templates
   *
   * PATTERN: Bulk delete for reset operations
   *
   * Used by EmailTemplateService.resetToDefaults() to clear all templates
   * before reseeding with defaults.
   *
   * ⚠️ DESTRUCTIVE: Deletes all templates in the table
   *
   * @returns Promise<void>
   */
  async deleteAll(): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (PostgreSQL trick)

    if (error) throw error;
  }
};
